import { javascript } from '@codemirror/lang-javascript';
import CodeMirror from '@uiw/react-codemirror';
import { xml } from '@codemirror/lang-xml';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { sql } from '@codemirror/lang-sql';
import { StreamLanguage } from '@codemirror/language';
import { csharp } from '@codemirror/legacy-modes/mode/clike';
import { githubDark, vscodeDark, dracula, nord, tokyoNight, materialDark } from '@uiw/codemirror-themes-all';
import { Tooltip } from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import JxSelect from '../JxSelect/JxSelect';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';
import KeybindingsTooltipContent from './KeybindingsTooltipContent';
import './JxCodeEditor.css';

// Maps a lang key to its CodeMirror6 language extension factory.
// Unknown/'text' keys fall through to plain editing, no highlighting.
const LANG_EXTENSIONS = {
    csharp: () => StreamLanguage.define(csharp),
    xml: () => xml(),
    css: () => css(),
    html: () => html(),
    sql: () => sql(),
    js: () => javascript(),
    jsx: () => javascript({ jsx: true }),
};

// Selectable CM6 themes. 'dark'/'light' are @uiw/react-codemirror's built-ins (string, no import),
// the rest come from @uiw/codemirror-themes-all as Extension objects.
export const THEME_OPTIONS = [
    { label: 'Dark (default)', value: 'dark' },
    { label: 'Light', value: 'light' },
    { label: 'GitHub Dark', value: 'githubDark' },
    { label: 'VS Code Dark', value: 'vscodeDark' },
    { label: 'Dracula', value: 'dracula' },
    { label: 'Nord', value: 'nord' },
    { label: 'Tokyo Night', value: 'tokyoNight' },
    { label: 'Material Dark', value: 'materialDark' },
];

const NAMED_THEMES = { githubDark, vscodeDark, dracula, nord, tokyoNight, materialDark };

/** Resolves a THEME_OPTIONS value to what CodeMirror's `theme` prop expects. */
const resolveTheme = (key) => NAMED_THEMES[key] ?? key; // 'dark'/'light' stay as-is (built-in strings)

/**
 * JxCodeEditor — standalone, reusable CodeMirror6 code editor.
 * Pure painter: caller owns both the value (onChange) and the theme (theme + onThemeChange).
 * No knowledge of where the code comes from (template note, generated file, pasted block, ...)
 * and no knowledge of user-settings/persistence — that's entirely the caller's responsibility.
 *
 * Comment toggle (Mod-/, Alt-A block comment) needs no extra extension — since
 * @codemirror/commands 0.20, those bindings live in defaultKeymap itself, which basicSetup
 * (used by @uiw/react-codemirror by default) already includes. commentKeymap no longer exists
 * as a separate export.
 *
 * Tooltip: MUI Tooltip + tooltipSxElevated (components/shared/jernyTooltipStyling.js) —
 * niet JxTooltip (deprecated, zie Development.Patterns rk=ed2d2d36-15de-4867-8b09-552a7801b626).
 * @param {string} value
 * @param {(value: string) => void} onChange
 * @param {string} [lang='text'] - one of LANG_EXTENSIONS keys, unknown key = plain text
 * @param {boolean} [readOnly=false]
 * @param {string} [id]
 * @param {string} [theme='dark'] - one of THEME_OPTIONS values; caller owns persistence
 * @param {(theme: string) => void} [onThemeChange] - when omitted, the theme picker is hidden
 */
export default function JxCodeEditor({ value, onChange, lang = 'text', readOnly = false, id, theme = 'dark', onThemeChange }) {
    const makeExt = LANG_EXTENSIONS[lang];
    const extensions = makeExt ? [makeExt()] : [];

    return (
        <div id={id} className="jx-code-editor">
            {onThemeChange && (
                <div className="jx-code-editor-toolbar">
                    <span className="jx-code-editor-toolbar-label">Theme</span>
                    <JxSelect
                        id={id ? `${id}_theme` : undefined}
                        value={theme}
                        onChange={onThemeChange}
                        options={THEME_OPTIONS}
                        defaultLabel="Theme"
                    />
                    <Tooltip title={<KeybindingsTooltipContent />} placement="bottom" componentsProps={tooltipSxElevated}>
                        <button type="button" className="jx-code-editor-kb-btn" aria-label="Keyboard shortcuts">
                            <KeyboardIcon sx={{ fontSize: 15 }} />
                        </button>
                    </Tooltip>
                </div>
            )}
            <div className="jx-code-editor-body jx-code-editor-scroll">
                <CodeMirror
                    value={value}
                    height="100%"
                    style={{ height: '100%' }}
                    theme={resolveTheme(theme)}
                    extensions={extensions}
                    readOnly={readOnly}
                    onChange={(next) => onChange?.(next)}
                />
            </div>
        </div>
    );
}
